import { AxiosError } from 'axios'
type ApiErrorPayload =
  | string
  | {
      title?: string
      message?: string
      error?: string | { message?: string }
      details?: string
    }
  | undefined
const hasText = (value?: string) => Boolean(value && value.trim().length > 0)

const CLOUDFLARE_INTERFERENCE_MESSAGE =
  'Cloudflare blocked or challenged this request. Turn off VPN if enabled, refresh, or ask an admin to skip Bot Fight / Managed Challenge for /api/ on the login host.'

function looksLikeHtmlDocument(text: string): boolean {
  const head = text.slice(0, 1200).toLowerCase()
  return (
    head.includes('<!doctype html') ||
    head.includes('<html') ||
    /<(body|head|title|div|meta|script)\b/.test(head)
  )
}

function isCloudflareInterferencePage(text: string): boolean {
  return /cloudflare|challenges\.cloudflare|cf-error|cf-wrapper|cf_chl|__cf_chl|just a moment|you have been blocked|attention required|enable javascript and cookies to continue/i.test(
    text
  )
}

/** Strip gateway HTML (Cloudflare WAF / challenge, nginx) so UI never dumps raw pages. */
export function sanitizeApiErrorText(text: string, fallback: string): string {
  const trimmed = text.trim()
  if (!trimmed) return fallback
  if (looksLikeHtmlDocument(trimmed) || isCloudflareInterferencePage(trimmed)) {
    return isCloudflareInterferencePage(trimmed)
      ? CLOUDFLARE_INTERFERENCE_MESSAGE
      : fallback
  }
  return trimmed
}

const readResponseBodyText = (data: unknown): string | undefined => {
  if (typeof data === 'string' && hasText(data)) return data.trim()
  if (data == null) return undefined
  if (typeof data !== 'object') {
    const text = String(data).trim()
    return hasText(text) && text !== '[object Object]' ? text : undefined
  }
  return undefined
}

export const extractApiErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof AxiosError) {
    const payload = error.response?.data as ApiErrorPayload
    const bodyText = readResponseBodyText(payload)
    if (bodyText) return sanitizeApiErrorText(bodyText, fallback)

    if (payload && typeof payload === 'object') {
      if (hasText(payload.title)) return payload.title!.trim()
      if (hasText(payload.message)) return payload.message!.trim()
      if (typeof payload.error === 'string' && hasText(payload.error)) return payload.error.trim()
      if (payload.error && typeof payload.error === 'object' && hasText(payload.error.message)) {
        return payload.error.message!.trim()
      }
      if (hasText(payload.details)) return payload.details!.trim()
    }

    const statusText = error.response?.statusText?.trim()
    if (
      statusText &&
      statusText !== 'Bad Request' &&
      statusText !== 'Internal Server Error'
    ) {
      return statusText
    }

    if (hasText(error.message) && !/^Request failed with status code \d+$/i.test(error.message)) {
      return sanitizeApiErrorText(error.message, fallback)
    }
  }

  if (error instanceof Error && hasText(error.message)) {
    return sanitizeApiErrorText(error.message, fallback)
  }

  return fallback
}

/** Account create flows: map duplicate-email API/DB errors to a clear toast. */
export const extractAccountCreateErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  const message = extractApiErrorMessage(error, fallback)
  if (
    /email already in use/i.test(message) ||
    (/unique/i.test(message) && /email/i.test(message)) ||
    /idx_users_email/i.test(message)
  ) {
    return 'Email already in use.'
  }
  return message
}
